import React, { useCallback, useEffect, useMemo, useState } from "react";
import BeginnerSidebar from "./BeginnerSidebar";
import { getProfile, updateUserProfile } from "../api/client";
import "../styles/practice.css";

const getProfileObject = (response) => {
  const data = response?.data ?? {};
  const user = data.user || data.profile || data.student_profile || data.studentprofile || {};
  return {
    ...data,
    ...user,
    student_profile: user.student_profile || data.student_profile || data.studentprofile || data.profile?.student_profile,
  };
};

const getStudentClass = (profile) => {
  const candidates = [
    profile?.student_profile?.class_level,
    profile?.student_profile?.class_level_display,
    profile?.student_profile?.class_name,
    profile?.student_profile?.student_class,
    profile?.student_profile?.grade,
    profile?.studentprofile?.class_level,
    profile?.studentprofile?.class_level_display,
    profile?.studentprofile?.class_name,
    profile?.studentprofile?.student_class,
    profile?.studentprofile?.grade,
    profile?.profile?.class_level,
    profile?.profile?.class_name,
    profile?.class_level,
    profile?.class_level_display,
    profile?.class_name,
    profile?.student_class,
    profile?.grade,
    profile?.class,
  ];

  const value = candidates.find((item) => item !== undefined && item !== null && String(item).trim() !== "");
  if (value === undefined) return "—";

  if (typeof value === "object") {
    const nested = value.name ?? value.title ?? value.label ?? value.class_level ?? value.id;
    return nested === undefined || nested === null ? "—" : formatClass(nested);
  }

  return formatClass(value);
};

const formatClass = (value) => {
  const text = String(value).trim();
  if (!text) return "—";
  if (/^class\\s*\\d+$/i.test(text)) return text.replace(/^class\\s*/i, "Class ");
  if (/^\\d+$/.test(text)) return `Class ${text}`;
  return text;
};

const errorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (data?.detail) return data.detail;
  if (data?.message) return data.message;
  if (data && typeof data === "object") {
    return Object.entries(data)
      .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(" ") : value}`)
      .join(" ");
  }
  return error?.message || "Unable to connect to your profile. Please try again.";
};

export default function BeginnerProfile() {
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState({ first_name: "", last_name: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getProfile();
      const data = getProfileObject(response);
      setProfile(data);
      setDraft({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
      });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const fullName = useMemo(() => {
    const composed = [draft.first_name, draft.last_name].filter(Boolean).join(" ").trim();
    return profile?.full_name || profile?.name || composed || profile?.username || profile?.email || "Beginner Student";
  }, [profile, draft.first_name, draft.last_name]);

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    try {
      // The existing shared profile endpoint handles the logged-in user.
      // Only standard Django User name fields are sent; email/class are read-only here.
      await updateUserProfile({
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
      });
      const refreshed = await getProfile();
      const data = getProfileObject(refreshed);
      setProfile(data);
      setDraft({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
      });
      setEditing(false);
      setMessage("Your profile was updated successfully.");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="beginner-portal">
      <BeginnerSidebar />
      <main className="beginner-portal-content">
        <header className="beginner-page-header">
          <div>
            <p className="beginner-eyebrow">BEGINNER STUDENT PORTAL</p>
            <h1>My Profile</h1>
            <p>View and update your account information.</p>
          </div>
          {!loading && profile && !editing && (
            <button type="button" className="btn btn-primary" onClick={() => setEditing(true)}>
              Edit profile
            </button>
          )}
        </header>

        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        {message && <div className="alert alert-success" role="status">{message}</div>}

        {loading ? (
          <section className="beginner-profile-card" aria-live="polite">Loading your profile…</section>
        ) : !profile ? (
          <section className="beginner-profile-card">
            <h2>Profile unavailable</h2>
            <p>We could not load your account details from Django.</p>
            <button type="button" className="btn btn-outline-primary" onClick={loadProfile}>Try again</button>
          </section>
        ) : (
          <>
            <section className="beginner-profile-card">
              <div className="beginner-profile-avatar"><i className="bi bi-person-circle" /></div>
              <div className="beginner-profile-details">
                <h2>{fullName}</h2>
                <p>{profile.email || profile.user_email || "Email not available"}</p>
                <span className="beginner-profile-badge">Beginner Student</span>
              </div>
            </section>

            {editing ? (
              <section className="practice-setup-card mt-3">
                <h2>Edit profile</h2>
                <form onSubmit={saveProfile} className="beginner-profile-form">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="beginner-first-name">First name</label>
                    <input id="beginner-first-name" className="form-control" name="first_name" value={draft.first_name} maxLength={150} onChange={(e) => setDraft((current) => ({ ...current, first_name: e.target.value }))} />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="beginner-last-name">Last name</label>
                    <input id="beginner-last-name" className="form-control" name="last_name" value={draft.last_name} maxLength={150} onChange={(e) => setDraft((current) => ({ ...current, last_name: e.target.value }))} />
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : "Save changes"}</button>
                    <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={() => { setDraft({ first_name: profile.first_name || "", last_name: profile.last_name || "" }); setEditing(false); setError(""); }}>Cancel</button>
                  </div>
                </form>
              </section>
            ) : (
              <section className="practice-setup-card mt-3">
                <h2>Account details</h2>
                <dl className="row mb-0">
                  <dt className="col-sm-4">Username</dt><dd className="col-sm-8">{profile.username || "—"}</dd>
                  <dt className="col-sm-4">Email</dt><dd className="col-sm-8">{profile.email || profile.user_email || "—"}</dd>
                  <dt className="col-sm-4">Class</dt><dd className="col-sm-8">{getStudentClass(profile)}</dd>
                  <dt className="col-sm-4">Account type</dt><dd className="col-sm-8">Beginner Student</dd>
                </dl>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
